import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Container, Form, Modal, Row, Tab, Tabs } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaEnvelope, FaExchangeAlt, FaPaperclip, FaTimesCircle, FaLink, FaReply, FaSync, FaEyeSlash, FaEye } from 'react-icons/fa';
import branchService from '../services/branchService';
import ticketService from '../services/ticketService';
import userService from '../services/userService';
import incomingEmailService from '../services/incomingEmailService';
import { formatDate, formatDateTime, formatTime } from '../utils/format';
import { ISSUE_TYPE_OPTIONS as ISSUE_TYPES } from '../utils/issueTypes';

const statusVariant = (status) => {
  switch (status) {
    case 'converted':
      return 'success';
    case 'linked':
      return 'primary';
    case 'replied':
      return 'info';
    case 'reviewed':
      return 'info';
    case 'rejected':
      return 'secondary';
    case 'pending':
      return 'warning';
    default:
      return 'warning';
  }
};

const statusLabel = (status) => {
  switch (status) {
    case 'converted':
      return 'تم التحويل';
    case 'linked':
      return 'مرتبطة';
    case 'replied':
      return 'تم الرد';
    case 'reviewed':
      return 'تمت المراجعة';
    case 'rejected':
      return 'مرفوض';
    case 'pending':
      return 'قيد الانتظار';
    case 'hidden':
      return 'محذوف محليًا';
    default:
      return status;
  }
};

// NEW: Visibility status helper
const visibilityVariant = (visibility) => {
  switch (visibility) {
    case 'hidden':
      return 'dark';
    case 'archived':
      return 'secondary';
    default:
      return 'light';
  }
};

// Sanitize HTML to prevent XSS - production ready
const sanitizeHtml = (html) => {
  if (!html) return '';
  
  // If DOMPurify is available (loaded via script tag), use it
  if (typeof window !== 'undefined' && window.DOMPurify) {
    return window.DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'u', 'strong', 'em', 'a', 'ul', 'ol', 'li', 
                     'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code',
                     'table', 'thead', 'tbody', 'tr', 'th', 'td', 'img', 'span', 'div'],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'style', 'width', 'height', 'target'],
      ALLOW_DATA_ATTR: false,
      ADD_ATTR: ['target'] // Add target for links
    });
  }
  
  // Fallback: comprehensive sanitization without external library
  let sanitized = html;
  
  // Remove script tags and their content
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  
  // Remove event handlers (onclick, onerror, onload, etc.)
  sanitized = sanitized.replace(/\s+on\w+="[^"]*"/gi, '');
  sanitized = sanitized.replace(/\s+on\w+='[^']*'/gi, '');
  
  // Remove javascript: URLs
  sanitized = sanitized.replace(/href\s*=\s*["']?\s*javascript:[^"'\s]*/gi, '');
  
  // Remove data: URLs (potential XSS via data URIs)
  sanitized = sanitized.replace(/src\s*=\s*["']?\s*data:[^"'\s]*/gi, '');
  
  // Remove iframe tags
  sanitized = sanitized.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
  
  // Remove object/embed tags (Flash, etc.)
  sanitized = sanitized.replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '');
  sanitized = sanitized.replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '');
  
  // Remove form tags (prevent CSRF)
  sanitized = sanitized.replace(/<form\b[^<]*(?:(?!<\/form>)<[^<]*)*<\/form>/gi, '');
  
  // Remove expression attributes (IE specific XSS)
  sanitized = sanitized.replace(/\s+expression\s*\([^)]*\)/gi, '');
  
  // Add target="_blank" to all links for security
  sanitized = sanitized.replace(/<a\s+([^>]*)\s*>/gi, '<a $1 target="_blank" rel="noopener noreferrer">');
  
  return sanitized;
};

const IncomingEmailInbox = () => {
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [branches, setBranches] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [convertForm, setConvertForm] = useState({
    branch_id: '',
    issue_types: [],
    priority: 'medium',
    short_description: '',
    ticket_description: '',
    requester_name: '',
    requester_email: '',
    internal_notes: '',
    assigned_technicians: []
  });
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [replyForm, setReplyForm] = useState({
    to: '',
    subject: '',
    text: '',
    html: ''
  });
  const [linkForm, setLinkForm] = useState({
    ticket_id: ''
  });
  
  const [filters, setFilters] = useState({ status: '', search: '', visibility: 'visible' });
  const [showHiddenSection, setShowHiddenSection] = useState(false);

  const fetchInbox = useCallback(async () => {
    setLoading(true);
    try {
      const data = await incomingEmailService.getAll(filters, { page: 1, limit: 50 });
      setEmails(data.emails || []);
      if (!selectedEmail && data.emails?.length) {
        setSelectedEmail(data.emails[0]);
      } else if (selectedEmail) {
        const updated = (data.emails || []).find((e) => e.id === selectedEmail.id);
        if (updated) setSelectedEmail(updated);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters, selectedEmail]);

  const fetchBranches = async () => {
    try {
      const data = await branchService.getAll();
      setBranches(data.branches || data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchTickets = async () => {
    try {
      const data = await ticketService.getAll({ status: 'new,in_progress,on_hold', limit: 100 });
      setTickets(data.tickets || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await incomingEmailService.syncFromMailbox();
      await fetchInbox();
      alert('تم مزامنة الرسائل بنجاح');
    } catch (error) {
      console.error(error);
      alert('فشلت المزامنة');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchTickets();
  }, []);

  useEffect(() => {
    fetchInbox();
  }, [filters.status, filters.visibility]);

  useEffect(() => {
    const t = setTimeout(fetchInbox, 350);
    return () => clearTimeout(t);
  }, [filters.search]);

  const attachments = useMemo(() => {
    if (!selectedEmail) return [];
    return Array.isArray(selectedEmail.attachments_json) ? selectedEmail.attachments_json : [];
  }, [selectedEmail]);

  const openConvert = async () => {
    if (!selectedEmail) return;
    setConvertForm({
      branch_id: '',
      issue_types: [],
      priority: 'medium',
      short_description: selectedEmail.subject || '',
      ticket_description: selectedEmail.body_text || '',
      requester_name: selectedEmail.requester_name || selectedEmail.sender_name || '',
      requester_email: selectedEmail.requester_email || selectedEmail.sender_email || '',
      internal_notes: '',
      assigned_technicians: []
    });
    
    if (assignableUsers.length === 0) {
      try {
        const data = await userService.getTicketAssignableUsers();
        setAssignableUsers(data.users || data || []);
      } catch (err) {
        console.error('Failed to fetch assignable users', err);
      }
    }
    
    setShowConvertModal(true);
  };

  const openReply = () => {
    if (!selectedEmail) return;
    setReplyForm({
      to: selectedEmail.sender_email || selectedEmail.requester_email || '',
      subject: `Re: ${selectedEmail.subject || ''}`,
      text: '',
      html: ''
    });
    setShowReplyModal(true);
  };

  const openLink = () => {
    if (!selectedEmail) return;
    setLinkForm({
      ticket_id: selectedEmail.linked_ticket_id || selectedEmail.converted_ticket_id || ''
    });
    setShowLinkModal(true);
  };

  const submitConvert = async () => {
    if (!selectedEmail) return;
    try {
      await incomingEmailService.convertToTicket(selectedEmail.id, convertForm);
      setShowConvertModal(false);
      await fetchInbox();
      const refreshed = await incomingEmailService.getById(selectedEmail.id);
      setSelectedEmail(refreshed);
      alert('تم تحويل البريد إلى تذكرة بنجاح');
    } catch (error) {
      alert(error.response?.data?.error || 'فشل التحويل');
    }
  };

  const submitReply = async () => {
    if (!selectedEmail) return;
    try {
      await incomingEmailService.replyToEmail(selectedEmail.id, replyForm);
      setShowReplyModal(false);
      await fetchInbox();
      const refreshed = await incomingEmailService.getById(selectedEmail.id);
      setSelectedEmail(refreshed);
      alert('تم إرسال الرد بنجاح');
    } catch (error) {
      alert(error.response?.data?.error || 'فشل إرسال الرد');
    }
  };

  const submitLink = async () => {
    if (!selectedEmail || !linkForm.ticket_id) return;
    try {
      await incomingEmailService.linkToTicket(selectedEmail.id, linkForm.ticket_id);
      setShowLinkModal(false);
      await fetchInbox();
      const refreshed = await incomingEmailService.getById(selectedEmail.id);
      setSelectedEmail(refreshed);
      alert('تم ربط البريد بالتذكرة بنجاح');
    } catch (error) {
      alert(error.response?.data?.error || 'فشل الربط');
    }
  };

  const setReviewStatus = async (status) => {
    if (!selectedEmail) return;
    try {
      await incomingEmailService.markReviewStatus(selectedEmail.id, status);
      const refreshed = await incomingEmailService.getById(selectedEmail.id);
      setSelectedEmail(refreshed);
      fetchInbox();
    } catch (error) {
      alert(error.response?.data?.error || 'فشل تحديث الحالة');
    }
  };

  const downloadAttachment = (index) => {
    if (!selectedEmail) return;
    const attachment = Array.isArray(selectedEmail.attachments_json)
      ? selectedEmail.attachments_json[index]
      : null;

    incomingEmailService
      .downloadAttachment(selectedEmail.id, index, attachment?.file_name)
      .catch((error) => {
        alert(error.response?.data?.error || error.message || 'ÙØ´Ù„ ØªØ­Ù…ÙŠÙ„ Ø§Ù„Ù…Ø±ÙÙ‚');
      });
  };

  // NEW: Hide email locally
  const hideEmailLocally = async () => {
    if (!selectedEmail) return;
    try {
      await incomingEmailService.hideLocally(selectedEmail.id);
      alert('تم إخفاء الرسالة من واجهة النظام فقط. ستظل موجودة في بريدك الإلكتروني.');
      // Refresh list
      await fetchInbox();
      setSelectedEmail(null);
    } catch (error) {
      alert(error.response?.data?.error || 'فشل إخفاء الرسالة');
    }
  };

  // NEW: Restore hidden email
  const restoreEmailLocally = async (emailId) => {
    try {
      await incomingEmailService.restoreLocally(emailId);
      alert('تم استعادة الرسالة بنجاح');
      // Refresh list
      await fetchInbox();
      setSelectedEmail(null);
    } catch (error) {
      alert(error.response?.data?.error || 'فشل استعادة الرسالة');
    }
  };

  return (
    <Container fluid className="page-container incoming-email-page">
      <div className="dashboard-header">
        <div className="dashboard-header-content">
          <div className="dashboard-header-icon"><FaEnvelope /></div>
          <div>
            <h1 className="dashboard-header-title">صندوق البريد الوارد للدعم</h1>
            <p className="dashboard-header-subtitle">مراجعة البريد وتحويله يدويًا إلى تذكرة</p>
          </div>
        </div>
        <Button 
          variant="outline-primary" 
          onClick={handleSync} 
          disabled={syncing}
          className="sync-button"
        >
          <FaSync className={`ms-1 ${syncing ? 'fa-spin' : ''}`} />
          {syncing ? ' جاري المزامنة...' : ' مزامنة'}
        </Button>
      </div>

      <Row className="incoming-email-layout g-4">
        <Col lg={5} className="incoming-email-list-column">
          <Card className="dashboard-card mb-3 incoming-email-filter-card">
            <Card.Body>
              <Row className="g-2 incoming-email-filter-row">
                <Col>
                  <Form.Control
                    placeholder="بحث في العنوان/المحتوى/المرسل"
                    value={filters.search}
                    onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
                  />
                </Col>
                <Col xs={3}>
                  <Form.Select
                    value={filters.status}
                    onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
                  >
                    <option value="">كل الحالات</option>
                    <option value="pending">قيد الانتظار</option>
                    <option value="reviewed">تمت المراجعة</option>
                    <option value="linked">مرتبطة</option>
                    <option value="converted">تم التحويل</option>
                    <option value="replied">تم الرد</option>
                    <option value="rejected">مرفوض</option>
                  </Form.Select>
                </Col>
                <Col xs={3}>
                  <Form.Select
                    value={filters.visibility}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFilters(prev => ({ ...prev, visibility: val }));
                      setShowHiddenSection(val === 'hidden');
                    }}
                    title="تصفية حسب الظهور"
                  >
                    <option value="visible">النشطة</option>
                    <option value="all">الكل</option>
                    <option value="hidden">المحذوفة محليًا</option>
                  </Form.Select>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="dashboard-card incoming-email-list-card">
            <Card.Body className="p-0">
              <div className="table-responsive-wrapper responsive-table-wrapper" style={{ maxHeight: '430px', overflowY: 'auto' }}>
              <table className="modern-table incoming-email-table responsive-data-table mb-0">
                <thead style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th style={{ padding: '0.5rem' }}>المرسل</th>
                    <th style={{ padding: '0.5rem' }}>الموضوع</th>
                    <th style={{ padding: '0.5rem' }}>الاستلام</th>
                    <th style={{ padding: '0.5rem' }}>الحالة</th>
                  </tr>
                </thead>
                <tbody>
                  {(emails || []).map((email) => (
                    <tr
                      key={email.id}
                      style={{ 
                        cursor: 'pointer', 
                        background: selectedEmail?.id === email.id ? '#e9ecef' : undefined,
                        borderBottom: '1px solid #f0f0f0'
                      }}
                      onClick={async () => setSelectedEmail(await incomingEmailService.getById(email.id))}
                    >
                      <td data-label="المرسل" style={{ padding: '0.5rem' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{email.sender_name || '-'}</div>
                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>{email.sender_email}</small>
                      </td>
                      <td data-label="الموضوع" style={{ padding: '0.5rem' }}>
                        <div style={{ fontSize: '0.85rem', lineHeight: '1.4', color: '#2c3e50' }}>{email.subject || '-'}</div>
                        {email.attachments_json?.length > 0 && (
                          <small className="text-info" style={{ fontSize: '0.75rem' }}><FaPaperclip /> {email.attachments_json.length}</small>
                        )}
                      </td>
                      <td data-label="الاستلام" style={{ padding: '0.5rem' }}>
                        <div style={{ fontSize: '0.8rem' }}>{formatDate(email.email_received_at)}</div>
                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>{formatTime(email.email_received_at)}</small>
                      </td>
                      <td data-label="الحالة" style={{ padding: '0.5rem' }}>
                        <Badge bg={statusVariant(email.processing_status)}>
                          {statusLabel(email.processing_status)}
                        </Badge>
                        {(email.linked_ticket_id || email.converted_ticket_id) && (
                          <Link 
                            to={`/tickets/${email.linked_ticket_id || email.converted_ticket_id}`}
                            className="d-block small mt-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <FaLink /> تذكرة
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!loading && emails.length === 0 && (
                    <tr><td colSpan={4} className="text-center text-muted py-4">لا توجد رسائل</td></tr>
                  )}
                </tbody>
              </table>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7} className="incoming-email-detail-column">
          <Card className="dashboard-card incoming-email-detail-card">
            <Card.Body>
              {!selectedEmail ? (
                <p className="text-muted mb-0">اختر رسالة لعرض التفاصيل</p>
              ) : (
                <>
                  <div className="incoming-email-detail-head d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <div className="mb-1" style={{ fontSize: '1rem', fontWeight: 'bold' }}>{selectedEmail.subject || '(بدون عنوان)'}</div>
                      <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                        من: {selectedEmail.sender_name || '-'} ({selectedEmail.sender_email})
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                        التاريخ: {selectedEmail.email_received_date || formatDate(selectedEmail.email_received_at)} | الوقت: {selectedEmail.email_received_time || formatTime(selectedEmail.email_received_at)}
                      </div>
                    </div>
                    <Badge bg={statusVariant(selectedEmail.processing_status)}>
                      {statusLabel(selectedEmail.processing_status)}
                    </Badge>
                  </div>

                  <Card className="mb-2 bg-light border-0 email-content-container">
                    <Card.Body 
                      className="p-2"
                      dangerouslySetInnerHTML={{ 
                        __html: sanitizeHtml(selectedEmail.body_html || (selectedEmail.body_text ? selectedEmail.body_text.replace(/\n/g, '<br/>') : '-')) 
                      }}
                      style={{ maxHeight: '300px', overflow: 'auto', fontSize: '0.9rem' }}
                    />
                  </Card>

                  <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }} className="mb-1"><FaPaperclip className="ms-1" /> المرفقات ({attachments.length})</div>
                  {attachments.length === 0 ? (
                    <p className="text-muted">لا توجد مرفقات</p>
                  ) : (
                    <ul className="attachment-list">
                      {attachments.map((a, idx) => (
                        <li key={`${a.file_name || 'att'}-${idx}`}>
                          <Button variant="link" className="p-0" onClick={() => downloadAttachment(idx)}>
                            <FaPaperclip className="ms-1" />
                            {a.file_name || `attachment-${idx + 1}`}
                            <span className="text-muted ms-2">
                              ({a.file_size ? Math.round(a.file_size / 1024) + ' KB' : ''})
                            </span>
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="incoming-email-action-bar d-flex flex-wrap gap-2 mt-2">
                    <Button size="sm" variant="primary" onClick={openConvert} disabled={selectedEmail.processing_status === 'converted'}>
                      <FaExchangeAlt className="ms-1" /> تحويل إلى تذكرة
                    </Button>
                    <Button size="sm" variant="outline-primary" onClick={openReply}>
                      <FaReply className="ms-1" /> رد
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={openLink}>
                      <FaLink className="ms-1" /> ربط بتذكرة
                    </Button>
                    <Button size="sm" variant="outline-info" onClick={() => setReviewStatus('reviewed')}>
                      <FaCheckCircle className="ms-1" /> تمّت المراجعة
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => setReviewStatus('rejected')}>
                      <FaTimesCircle className="ms-1" /> غير صالح كتذكرة
                    </Button>
                    {/* NEW: Hide from system button */}
                    {selectedEmail.local_visibility_status !== 'hidden' && (
                      <Button 
                        size="sm"
                        variant="outline-dark" 
                        onClick={hideEmailLocally}
                        title="إخفاء الرسالة من واجهة النظام فقط، ستظل موجودة في بريدك الإلكتروني"
                      >
                        <FaEyeSlash className="ms-1" /> حذف من النظام
                      </Button>
                    )}
                    {/* NEW: Restore button for hidden emails */}
                    {selectedEmail.local_visibility_status === 'hidden' && (
                      <Button 
                        size="sm"
                        variant="outline-success" 
                        onClick={() => restoreEmailLocally(selectedEmail.id)}
                        title="استعادة الرسالة للظهور في الواجهة"
                      >
                        <FaEye className="ms-1" /> استعادة
                      </Button>
                    )}
                    {(selectedEmail.converted_ticket_id || selectedEmail.linked_ticket_id) && (
                      <Link 
                        to={`/tickets/${selectedEmail.converted_ticket_id || selectedEmail.linked_ticket_id}`} 
                        className="btn btn-sm btn-outline-success"
                      >
                        فتح التذكرة #{selectedEmail.converted_ticket_number || selectedEmail.linked_ticket_id}
                      </Link>
                    )}
                  </div>

                  <div className="mt-3 small">
                    <div>إرسال إشعار الاستلام: {selectedEmail.acknowledgment_email_sent ? 'تم' : 'لم يُرسل'}</div>
                    <div>إرسال بريد الحل: {selectedEmail.resolution_email_sent ? 'تم' : 'لم يُرسل'}</div>
                    {selectedEmail.source_message_id && (
                      <div className="text-muted">Message-ID: {selectedEmail.source_message_id}</div>
                    )}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Convert Modal */}
      <Modal show={showConvertModal} onHide={() => setShowConvertModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>تحويل البريد إلى تذكرة</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label>الفرع *</Form.Label>
              <Form.Select
                value={convertForm.branch_id}
                onChange={(e) => setConvertForm((p) => ({ ...p, branch_id: e.target.value }))}
              >
                <option value="">اختر الفرع</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>الأولوية</Form.Label>
              <Form.Select
                value={convertForm.priority}
                onChange={(e) => setConvertForm((p) => ({ ...p, priority: e.target.value }))}
              >
                <option value="low">منخفضة</option>
                <option value="medium">متوسطة</option>
                <option value="high">عالية</option>
                <option value="urgent">عاجلة</option>
              </Form.Select>
            </Col>
            <Col md={12}>
              <Form.Label>موضوع التذكرة *</Form.Label>
              <Form.Control
                value={convertForm.short_description}
                onChange={(e) => setConvertForm((p) => ({ ...p, short_description: e.target.value }))}
              />
            </Col>
            <Col md={12}>
              <Form.Label>الوصف</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={convertForm.ticket_description}
                onChange={(e) => setConvertForm((p) => ({ ...p, ticket_description: e.target.value }))}
              />
            </Col>
            <Col md={6}>
              <Form.Label>اسم مقدم الطلب</Form.Label>
              <Form.Control
                value={convertForm.requester_name}
                onChange={(e) => setConvertForm((p) => ({ ...p, requester_name: e.target.value }))}
              />
            </Col>
            <Col md={6}>
              <Form.Label>البريد الإلكتروني</Form.Label>
              <Form.Control
                value={convertForm.requester_email}
                onChange={(e) => setConvertForm((p) => ({ ...p, requester_email: e.target.value }))}
              />
            </Col>
            <Col md={12}>
              <Form.Label>نوع المشكلة *</Form.Label>
              <div className="border rounded p-2" style={{ maxHeight: 180, overflowY: 'auto' }}>
                {ISSUE_TYPES.map((it) => (
                  <Form.Check
                    key={it.value}
                    type="checkbox"
                    label={it.label}
                    checked={convertForm.issue_types.includes(it.value)}
                    onChange={(e) => {
                      setConvertForm((p) => ({
                        ...p,
                        issue_types: e.target.checked
                          ? [...p.issue_types, it.value]
                          : p.issue_types.filter((x) => x !== it.value)
                      }));
                    }}
                  />
                ))}
              </div>
            </Col>
            <Col md={12}>
              <Form.Label>ملاحظات داخلية</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={convertForm.internal_notes}
                onChange={(e) => setConvertForm((p) => ({ ...p, internal_notes: e.target.value }))}
              />
            </Col>
            <Col md={12}>
              <Form.Label>إسناد التذكرة (اختياري)</Form.Label>
              <div className="border rounded p-2" style={{ maxHeight: 180, overflowY: 'auto' }}>
                {assignableUsers.length === 0 ? (
                  <p className="text-muted small mb-0">جاري تحميل قائمة الفنيين والمدراء...</p>
                ) : (
                  assignableUsers.map((u) => (
                    <Form.Check
                      key={u.id}
                      type="checkbox"
                      id={`assignee-${u.id}`}
                      label={`${u.full_name} — ${u.role === 'admin' ? 'مدير' : u.role === 'supervisor' ? 'مشرف' : 'فني'}`}
                      checked={convertForm.assigned_technicians.includes(u.id)}
                      onChange={(e) => {
                        setConvertForm((p) => ({
                          ...p,
                          assigned_technicians: e.target.checked
                            ? [...p.assigned_technicians, u.id]
                            : p.assigned_technicians.filter((id) => id !== u.id)
                        }));
                      }}
                    />
                  ))
                )}
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConvertModal(false)}>إلغاء</Button>
          <Button variant="primary" onClick={submitConvert}>إنشاء التذكرة</Button>
        </Modal.Footer>
      </Modal>

      {/* Reply Modal */}
      <Modal show={showReplyModal} onHide={() => setShowReplyModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>الرد على البريد</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={12}>
              <Form.Label>إلى *</Form.Label>
              <Form.Control
                type="email"
                value={replyForm.to}
                onChange={(e) => setReplyForm((p) => ({ ...p, to: e.target.value }))}
              />
            </Col>
            <Col md={12}>
              <Form.Label>الموضوع</Form.Label>
              <Form.Control
                value={replyForm.subject}
                onChange={(e) => setReplyForm((p) => ({ ...p, subject: e.target.value }))}
              />
            </Col>
            <Col md={12}>
              <Form.Label>نص الرسالة *</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                value={replyForm.text}
                onChange={(e) => setReplyForm((p) => ({ ...p, text: e.target.value }))}
                placeholder="اكتب ردك هنا..."
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReplyModal(false)}>إلغاء</Button>
          <Button variant="primary" onClick={submitReply} disabled={!replyForm.text}>إرسال الرد</Button>
        </Modal.Footer>
      </Modal>

      {/* Link to Ticket Modal */}
      <Modal show={showLinkModal} onHide={() => setShowLinkModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>ربط البريد بتذكرة موجودة</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={12}>
              <Form.Label>اختر التذكرة *</Form.Label>
              <Form.Select
                value={linkForm.ticket_id}
                onChange={(e) => setLinkForm((p) => ({ ...p, ticket_id: e.target.value }))}
              >
                <option value="">اختر التذكرة</option>
                {tickets.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.ticket_number} - {t.short_description?.substring(0, 50)} ({t.status})
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLinkModal(false)}>إلغاء</Button>
          <Button variant="primary" onClick={submitLink} disabled={!linkForm.ticket_id}>ربط</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default IncomingEmailInbox;
